import { useEffect, type FunctionComponent, type ReactElement, useMemo } from 'react'
import { type ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import {
  extractUserContextFromJWT,
  logAuthenticationError,
  ERROR_TYPES,
  AUTH_STAGES,
  FAILURE_REASONS,
} from '../../utils/auth'
import { errors } from '../../utils/error-handling/errors'
import {
  useFetchCurrentEnterpriseAccountLazyQuery,
  type FetchCurrentEnterpriseAccountQuery,
} from '../../__codegen__/api'
import {
  isNotAuthorizedError,
  isUndeterminedAccountError,
  isTokenExpiredError,
} from '../utils/apollo/errors/isError.hooks'
import { useSessionData } from '../utils/apollo/session/useSessionData.hooks'
import { type PartialCurrentEnterpriseAccount } from './currentAccount.types'

export type CurrentAccountDetailsProps = Parameters<
  typeof useFetchCurrentEnterpriseAccountLazyQuery
>[0]

interface CurrentAccountDetailsValue {
  authorized: boolean
  actionRequired: boolean
  account: FetchCurrentEnterpriseAccountQuery['currentEnterpriseAccount']
  loading: boolean
  error?: ApolloError
  refetch: () => void
}

const notAuthorizedAccountDetails: Omit<CurrentAccountDetailsValue, 'loading'> = {
  authorized: false,
  actionRequired: false,
  account: undefined,
  error: undefined,
  refetch: () => {},
}

export const useCurrentAccountDetails = (
  options: CurrentAccountDetailsProps = {}
): CurrentAccountDetailsValue => {
  const { data: sessionData, setData: storeSessionData } = useSessionData()

  const isExpired = useMemo(() => {
    if (sessionData?.token) {
      // Try to extract user context before parsing fails
      const userContext = extractUserContextFromJWT(sessionData.token)

      try {
        const jwtParts = sessionData.token.split('.')
        // base64 decode the payload
        const tokenPayload = JSON.parse(atob(jwtParts[1]))

        // If no exp claim exists, treat as expired
        if (!tokenPayload.exp) {
          storeSessionData(undefined)
          setTimeout(() => {
            window.location.href = '/public/log-in?authError=sessionCorrupted'
          }, 0)
          return true
        }

        // exp is a unix timestamp
        return +new Date(tokenPayload.exp * 1000) < +new Date()
      } catch (error) {
        // Send detailed error to DataDog for monitoring
        logAuthenticationError(
          error as Error,
          ERROR_TYPES.JWT_PARSING_FAILURE,
          AUTH_STAGES.JWT_VALIDATION,
          FAILURE_REASONS.JWT_PARSE_ERROR,
          userContext,
          {
            tokenPreview: sessionData.token.substring(0, 20) + '...',
            jwtPartsCount: sessionData.token.split('.').length,
          }
        )

        // Clear corrupted session and redirect to login with error
        storeSessionData(undefined)

        // Use setTimeout to avoid redirecting during render
        setTimeout(() => {
          window.location.href = '/public/log-in?authError=sessionCorrupted'
        }, 0)
        return true // Force treated as expired
      }
    }
    return false
  }, [sessionData, storeSessionData])

  const hasValidSession = Boolean(sessionData && !isExpired)
  const hasSession = Boolean(sessionData)

  const [
    fetchAccount,
    { data: apiData, loading: apiLoading, error: apiError, called: apiCalled, refetch },
  ] = useFetchCurrentEnterpriseAccountLazyQuery(options)

  const notAuthorizedError = isNotAuthorizedError(apiError)
  const isSessionError =
    notAuthorizedError || isUndeterminedAccountError(apiError) || isTokenExpiredError(apiError)

  // If we have a session token (even if expired), make the API call.
  // The errorLink in clientUtilities.ts will handle token refresh if needed.
  useEffect(() => {
    if (hasSession) {
      fetchAccount()
    }
  }, [hasSession, fetchAccount])

  // If we fetched the account, but it failed, and it was a not-authorized error
  // Then our session must be invalid, and we should clear it
  // NOTE: We don't clear session for expired tokens here because the errorLink
  // in clientUtilities.ts will automatically attempt a refresh when the API call fails.
  // We only clear the session for authorization errors (invalid token, disabled account, etc.)
  useEffect(() => {
    if (notAuthorizedError) {
      const reason = FAILURE_REASONS.AUTHORIZATION_ERROR
      const errorCode = apiError?.graphQLErrors?.[0]?.extensions?.code

      // Try to extract user context before clearing session
      let userContext: { email: string | null; emailError?: string; userId: string } = {
        email: null,
        emailError: 'no_session',
        userId: 'unknown',
      }
      if (sessionData?.token) {
        userContext = extractUserContextFromJWT(sessionData.token)
      }

      // Send to DataDog for monitoring using centralized auth logging
      logAuthenticationError(
        new Error(`Session cleared due to authorization error: ${errorCode}`),
        ERROR_TYPES.SESSION_CLEAR_EVENT,
        AUTH_STAGES.SESSION_MANAGEMENT,
        FAILURE_REASONS.AUTHORIZATION_ERROR,
        userContext,
        {
          errorCode,
          reason,
          hasApiError: !!apiError,
          isTokenExpired: isExpired,
        }
      )

      storeSessionData(undefined)
    }
  }, [notAuthorizedError, isExpired, apiError, sessionData, storeSessionData])

  const account = apiData?.currentEnterpriseAccount

  // If we are authorized and have a account data, then setup datadog rum user details
  useEffect(() => {
    try {
      if (account) {
        // Set user identifying data for datadog rum
        const user = {
          id: account.id,
          // Add any other custom attributes you want to track
          canonicalId: account.canonicalId,
          isInternal: account.isInternal,
          isImpersonating: account.isImpersonating,
        }
        datadogRum.setUser(user)
      }
    } catch (error) {
      errors.captureException(error, 'Error setting Datadog RUM user details', {
        logToConsole: true,
      })
    }
  }, [account])

  // If we find out that we do not have a session, then we are done loading
  if (hasValidSession === false) {
    return { ...notAuthorizedAccountDetails, loading: false }
  }

  // If we haven't fetched the account details yet, then we are still in a "loading" state
  const loading = !apiCalled || apiLoading

  // We suppress session-related errors (unauthorized, expired, etc.)
  // This allows the app to stay in a "not logged in" state so that
  // NotLoggedInRedirect can handle the redirect to the login page.
  if (isSessionError) {
    return { ...notAuthorizedAccountDetails, loading }
  }

  // If there is an account and it isn't in a valid state, we need to finish setting it up
  const actionRequired = account ? !account?.accountStateValid : false

  return {
    authorized: Boolean(apiData),
    account,
    loading,
    error: apiError,
    actionRequired,
    refetch: refetch ?? (() => {}),
  }
}

type CurrentAccountWithChildren = (account: PartialCurrentEnterpriseAccount) => ReactElement
interface ICurrentAccountProps {
  children: CurrentAccountWithChildren
}

/**
 * This is a helper component that will render the children with the current account details data
 * This is only required for legacy components that are not using hooks (i.e. StripeAssociations.tsx)
 */
export const CurrentAccountDetails: FunctionComponent<
  React.PropsWithChildren<ICurrentAccountProps>
> = ({ children }) => {
  const { account } = useCurrentAccountDetails()

  return children(account)
}
